/**
 * Subscription + role-aware dashboard views.
 * Three distinct homes: Executive, Operational, Finance View.
 *
 * Gates (same SSOT as sidebar):
 * 1. subscription.sidebar allowlist (via navAccessMap)
 * 2. org role / full-shell / analytics module (via effective-access + role keywords)
 */

import {
  GATED_DASHBOARD_NAV_IDS,
  hasFullPlatformShellAccess,
  subscriptionAllowsNavItem,
} from '../config/navAccessMap';

export const DASHBOARD_VIEWS = {
  EXECUTIVE: 'executive',
  OPERATIONAL: 'operational',
  FINANCE: 'finance',
};

export const DASHBOARD_VIEW_NAV_IDS = {
  [DASHBOARD_VIEWS.EXECUTIVE]: 'dashboard-executive',
  [DASHBOARD_VIEWS.OPERATIONAL]: 'dashboard-operational',
  [DASHBOARD_VIEWS.FINANCE]: 'dashboard-finance',
};

export const DASHBOARD_PREFERENCE_KEY = 'dashboard_view_preference';

const ADMIN_ROLE_KEYWORDS = new Set([
  'owner',
  'admin',
  'super_admin',
  'superadmin',
  'practice_owner',
  'org_admin',
  'organization_admin',
]);

const EXECUTIVE_ROLE_KEYWORDS = new Set([
  'executive',
  'ceo',
  'cro',
  'cmo',
  'coo',
  'practice_manager',
  'director',
  'vp',
  'president',
  'c_suite',
]);

const FINANCE_ROLE_KEYWORDS = new Set([
  'cfo',
  'controller',
  'finance',
  'finance_director',
  'finance_manager',
  'billing_manager',
]);

const EXECUTIVE_TITLE_KEYWORDS = [
  'chief executive',
  'chief operating',
  'chief medical',
  'chief revenue',
  'ceo',
  'cro',
  'cmo',
  'coo',
  'president',
  'vice president',
  'vp ',
  'director',
  'practice manager',
];

const FINANCE_TITLE_KEYWORDS = [
  'cfo',
  'chief financial',
  'controller',
  'finance director',
  'finance manager',
  'billing manager',
];

function normalizeRole(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

function roleCandidates(user, orgRole) {
  const list = [
    orgRole,
    user?.role,
    user?.organization_role,
    user?.current_user_role,
    ...(Array.isArray(user?.roles) ? user.roles : []),
  ];
  return list.map(normalizeRole).filter(Boolean);
}

function titleText(user) {
  return String(user?.job_title || user?.title || '').toLowerCase();
}

export function isAdminRole(user, options = {}) {
  const roles = roleCandidates(user, options.orgRole);
  return roles.some(
    (r) => ADMIN_ROLE_KEYWORDS.has(r) || r === 'admin' || r.endsWith('_admin') || r.includes('owner')
  );
}

export function isFinanceRole(user, options = {}) {
  const roles = roleCandidates(user, options.orgRole);
  if (roles.some((r) => FINANCE_ROLE_KEYWORDS.has(r) || r.includes('finance') || r.includes('cfo') || r.includes('controller'))) {
    return true;
  }
  const title = titleText(user);
  return Boolean(title && FINANCE_TITLE_KEYWORDS.some((kw) => title.includes(kw)));
}

export function isExecutiveRole(user, options = {}) {
  const roles = roleCandidates(user, options.orgRole);
  if (roles.some((r) => EXECUTIVE_ROLE_KEYWORDS.has(r) || r.includes('executive') || r.includes('c-suite'))) {
    return true;
  }
  const title = titleText(user);
  if (!title) return false;
  if (FINANCE_TITLE_KEYWORDS.some((kw) => title.includes(kw)) && !title.includes('chief executive')) {
    return false;
  }
  return EXECUTIVE_TITLE_KEYWORDS.some((kw) => title.includes(kw));
}

function accessHasAnalytics(access) {
  return (access?.applications || []).some((a) => (a.name || '').toLowerCase() === 'analytics');
}

function roleAllowsDashboardView(view, user, options = {}) {
  if (isAdminRole(user, options)) return true;
  if (view === DASHBOARD_VIEWS.EXECUTIVE) return isExecutiveRole(user, options);
  if (view === DASHBOARD_VIEWS.FINANCE) return isFinanceRole(user, options);
  return false;
}

/**
 * Operational: all users.
 * Executive / Finance: subscription allowlist (when present) ∩
 * (full platform shell | analytics module | org role keywords).
 */
export function canAccessDashboardView(view, user, options = {}) {
  if (view === DASHBOARD_VIEWS.OPERATIONAL) return true;

  const navId = DASHBOARD_VIEW_NAV_IDS[view];
  if (!navId || !GATED_DASHBOARD_NAV_IDS.has(navId)) return false;

  const access = options.access;
  if (access && !access.loading) {
    if (!subscriptionAllowsNavItem(navId, access)) return false;
    if (hasFullPlatformShellAccess(access) || accessHasAnalytics(access)) return true;
  }

  return roleAllowsDashboardView(view, user, options);
}

export function getAllowedDashboardViews(user, options = {}) {
  return [DASHBOARD_VIEWS.EXECUTIVE, DASHBOARD_VIEWS.OPERATIONAL, DASHBOARD_VIEWS.FINANCE].filter((view) =>
    canAccessDashboardView(view, user, options)
  );
}

export function getStoredDashboardPreference() {
  if (typeof window === 'undefined') return 'auto';
  return localStorage.getItem(DASHBOARD_PREFERENCE_KEY) || 'auto';
}

export function setStoredDashboardPreference(preference) {
  if (typeof window === 'undefined') return;
  const value = preference || 'auto';
  localStorage.setItem(DASHBOARD_PREFERENCE_KEY, value);
  window.dispatchEvent(new CustomEvent('dashboard-preference-updated', { detail: { preference: value } }));
}

function defaultViewForRole(user, options = {}) {
  if (isAdminRole(user, options)) return DASHBOARD_VIEWS.EXECUTIVE;
  if (isFinanceRole(user, options) && !isExecutiveRole(user, options)) return DASHBOARD_VIEWS.FINANCE;
  if (isExecutiveRole(user, options)) return DASHBOARD_VIEWS.EXECUTIVE;
  if (isFinanceRole(user, options)) return DASHBOARD_VIEWS.FINANCE;
  return DASHBOARD_VIEWS.OPERATIONAL;
}

export function resolveDashboardView(user, preference = getStoredDashboardPreference(), options = {}) {
  const allowed = getAllowedDashboardViews(user, options);
  const roleDefault = defaultViewForRole(user, options);
  const fallback = allowed.includes(roleDefault) ? roleDefault : allowed[0] || DASHBOARD_VIEWS.OPERATIONAL;

  let preferred = fallback;
  if (preference === DASHBOARD_VIEWS.EXECUTIVE) preferred = DASHBOARD_VIEWS.EXECUTIVE;
  else if (preference === DASHBOARD_VIEWS.OPERATIONAL) preferred = DASHBOARD_VIEWS.OPERATIONAL;
  else if (preference === DASHBOARD_VIEWS.FINANCE) preferred = DASHBOARD_VIEWS.FINANCE;

  return allowed.includes(preferred) ? preferred : fallback;
}

export function getDashboardRoutes() {
  return {
    landing: '/',
    executive: '/dashboard/executive',
    operational: '/dashboard/operational',
    finance: '/dashboard/finance',
  };
}

export function routeForDashboardView(view) {
  const routes = getDashboardRoutes();
  if (view === DASHBOARD_VIEWS.FINANCE) return routes.finance;
  if (view === DASHBOARD_VIEWS.EXECUTIVE) return routes.executive;
  return routes.operational;
}

export function dashboardViewLabel(view) {
  if (view === DASHBOARD_VIEWS.FINANCE) return 'Finance View';
  if (view === DASHBOARD_VIEWS.OPERATIONAL) return 'Operational';
  return 'Executive';
}
